import { Button } from "@blueprintjs/core";
import classnames from "classnames";
import React, { useState } from "react";
import {
    formatFuelTypeForBusPage,
    formatPhaseStatus,
    IN_SCOPE_LABEL,
    NOT_IN_SCOPE_LABEL,
    RelatedProjectOnConstraint
} from "types/queueProjectRelatednessType";
import "./ConstraintExpandPanel.scss";
import { ScoreBucketTag } from "./ScoreBucketTag";

// Rows shown before the "Show N more" affordance kicks in.
const INITIAL_VISIBLE_ROWS = 5;

const formatDfaxPercent = (dfax: number): string => {
    return `${(Math.abs(dfax) * 100).toFixed(1)}%`;
};

const EMPTY_CELL = "–";

const ProjectRow: React.FC<{
    readonly project: RelatedProjectOnConstraint;
    readonly showHops: boolean;
}> = ({ project, showHops }) => {
    const cycle = [
        project.cluster,
        project.status !== undefined
            ? formatPhaseStatus(project.status)
            : undefined
    ]
        .filter((s): s is string => s !== undefined && s.length > 0)
        .join(" · ");

    return (
        <div
            className={classnames(
                "ConstraintExpandPanel-grid-row",
                "ConstraintExpandPanel-row"
            )}
        >
            <span className="ConstraintExpandPanel-project-number">
                {project.projectNumber}
            </span>
            <span className="ConstraintExpandPanel-cycle">
                {cycle.length > 0 ? cycle : EMPTY_CELL}
            </span>
            {/* Muted regardless of value so the column reads uniformly;
                the wording (and the dashed impact tag) carries the
                in/out-of-scope distinction, matching the sidebar panel. */}
            <span className="bp5-text-muted">
                {/* Em-dash only while membership is unknown (loading or
                    lookup unavailable) — a definitive out-of-scope answer
                    is labeled explicitly rather than left blank. */}
                {project.isInCurrentScope === undefined
                    ? EMPTY_CELL
                    : project.isInCurrentScope
                    ? IN_SCOPE_LABEL
                    : NOT_IN_SCOPE_LABEL}
            </span>
            <span className="ConstraintExpandPanel-fuel-label">
                {formatFuelTypeForBusPage(project.fuelType)}
            </span>
            <span className="ConstraintExpandPanel-num">
                {project.erisCapacityMw.toFixed(0)} MW
            </span>
            {showHops && (
                <span className="ConstraintExpandPanel-num">
                    {project.numHops ?? EMPTY_CELL}
                </span>
            )}
            <span className="ConstraintExpandPanel-num">
                {formatDfaxPercent(project.projectDfax)}
            </span>
            <span className="ConstraintExpandPanel-impact">
                <ScoreBucketTag
                    score={project.overallScore}
                    notInScope={project.isInCurrentScope === false}
                />
            </span>
        </div>
    );
};

/**
 * A single label/value pair for the constraint-details strip: table
 * columns the bus page hides in compact mode and surfaces here instead.
 */
export interface ConstraintDetailEntry {
    readonly label: string;
    readonly value: string;
}

// Rendered in the same bordered-ledger style as the related-projects
// list below it (shared classes within this component) so the two
// sections of an expanded row read as one consistent panel.
const ConstraintDetailsStrip: React.FC<{
    readonly details: ReadonlyArray<ConstraintDetailEntry>;
}> = ({ details }) => (
    <div className="ConstraintExpandPanel-rows">
        <div className="ConstraintExpandPanel-heading-row">
            <h6 className="bp5-heading ConstraintExpandPanel-heading">
                Constraint details
            </h6>
        </div>
        <div className="ConstraintExpandPanel-details-row">
            {details.map((entry) => (
                <div key={entry.label} className="ConstraintExpandPanel-detail">
                    <span className="bp5-text-small bp5-text-muted ConstraintExpandPanel-detail-label">
                        {entry.label}
                    </span>
                    <span className="ConstraintExpandPanel-detail-value">
                        {entry.value}
                    </span>
                </div>
            ))}
        </div>
    </div>
);

const ConstraintExpandPanel: React.FC<{
    // Absent (vs empty) when the page has no relatedness data at all,
    // e.g. non-MISO regions — the projects section is omitted entirely.
    readonly projects?: ReadonlyArray<RelatedProjectOnConstraint>;
    readonly details?: ReadonlyArray<ConstraintDetailEntry>;
}> = ({ projects, details }) => {
    const [showAll, setShowAll] = useState(false);

    const detailEntries = details ?? [];
    const projectRows = projects ?? [];

    if (detailEntries.length === 0 && projectRows.length === 0) {
        return (
            <div className="ConstraintExpandPanel">
                <p className="bp5-text-muted ConstraintExpandPanel-empty">
                    No related queue projects on this constraint.
                </p>
            </div>
        );
    }

    // Hops is a supplementary lookup that can be unavailable (loading or
    // not deployed); only show the column when at least one row has data.
    const showHops = projectRows.some(
        (project) => project.numHops !== undefined
    );

    const visibleProjects = showAll
        ? projectRows
        : projectRows.slice(0, INITIAL_VISIBLE_ROWS);
    const hiddenCount = projectRows.length - visibleProjects.length;

    return (
        <div className="ConstraintExpandPanel">
            {detailEntries.length > 0 && (
                <ConstraintDetailsStrip details={detailEntries} />
            )}
            {projects !== undefined && (
                <div className="ConstraintExpandPanel-rows-scroll">
                    <div
                        className={classnames(
                            "ConstraintExpandPanel-rows",
                            "ConstraintExpandPanel-rows-grid",
                            "ConstraintExpandPanel-rows-wide",
                            {
                                "ConstraintExpandPanel-rows-grid-with-hops":
                                    showHops
                            }
                        )}
                    >
                        <div className="ConstraintExpandPanel-heading-row">
                            <h6 className="bp5-heading ConstraintExpandPanel-heading">
                                Related queue projects{" "}
                                <span className="bp5-text-muted ConstraintExpandPanel-heading-count">
                                    ({projectRows.length})
                                </span>
                            </h6>
                            {projectRows.length > 0 && (
                                <span className="bp5-text-small bp5-text-muted">
                                    Ranked by contribution to this constraint
                                </span>
                            )}
                        </div>
                        {projectRows.length === 0 && (
                            <div className="bp5-text-muted ConstraintExpandPanel-no-projects-row">
                                No related queue projects on this constraint.
                            </div>
                        )}
                        {projectRows.length > 0 && (
                            <div
                                className={classnames(
                                    "bp5-text-small",
                                    "ConstraintExpandPanel-grid-row",
                                    "ConstraintExpandPanel-header-row"
                                )}
                            >
                                <span>Project</span>
                                <span>Cycle</span>
                                <span>Status</span>
                                <span>Fuel</span>
                                <span className="ConstraintExpandPanel-num">
                                    Pmax
                                </span>
                                {showHops && (
                                    <span className="ConstraintExpandPanel-num">
                                        Bus hops
                                    </span>
                                )}
                                <span className="ConstraintExpandPanel-num">
                                    DFAX
                                </span>
                                <span className="ConstraintExpandPanel-impact">
                                    Impact on all constraints
                                </span>
                            </div>
                        )}
                        {visibleProjects.map((project) => (
                            <ProjectRow
                                key={project.projectNumber}
                                project={project}
                                showHops={showHops}
                            />
                        ))}
                        {(hiddenCount > 0 || showAll) && (
                            <div className="ConstraintExpandPanel-show-more-row">
                                <Button
                                    minimal
                                    small
                                    className="ConstraintExpandPanel-show-more"
                                    onClick={() => setShowAll(!showAll)}
                                >
                                    {showAll
                                        ? "Show less"
                                        : `Show ${hiddenCount} more...`}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConstraintExpandPanel;
